import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import MaterialTable from "material-table";
import { Row, Col } from "react-bootstrap";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import GYAxios from "../../../util/GYAxios";

import { dirname } from "../../../util/resourcePath";
import { useFormatter } from "../../../util/formatter";
import { CSVLink } from "react-csv";
import { stringify } from 'query-string';

import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../../util/momentTimezone";

import "react-datepicker/dist/react-datepicker.css";
import Noti from "../../../util/noti";
import { useUser } from "../../../util/user";

import style from './PaymentRecordVN.module.css';
import ReactSelect from "react-select";
import { allEntityOpt, allOpt, formatOldEntityId, oldEntityFilter } from "../../../util/invoice";


const PaymentRecordVN = (props) => {
	const firstFetch = useRef(true);
	const directory = dirname();
	const [formatNumber] = useFormatter();

	const user = useUser();
	const [ paymentRecordData, setPaymentRecordData ] = useState([]);
	const [ isLoading, setIsLoading ] = useState(true);

	// filter options
	const [solarEntityOptions, setSolarEntityOptions] = useState([]);
	const [clientOptions, setClientOptions] = useState([]);
	const paymentStatusOptions = useMemo(() => ([ allOpt, {label: 'Unpaid', value: 'UNPAID'}, {label: 'Overdued', value: 'OVERDUED'}, {label: 'Paid', value: 'PAID'} ]), []);
	// selected filter
	const [filter, setFilter] = useState({
		// binded with date select to dynamically calculate Aging Days in Material Table
		agingDate: moment().startOf('day'),
		solarEntity: '_ALL',
		client: '_ALL',
		paymentStatus: 'UNPAID',
		monthStart: null,
		monthEnd: null,
	});
	const prevFilter = useRef(null);
	// constraints
	const filterMonthMax = useMemo(() => new Date(), []);

	const getARAging = (item, {style=true}={}) => {
		if(!item.payment_date) {
			const agingDays = filter.agingDate.diff(moment(item.due_date), 'days');
			if(agingDays > 0) {
				return style ? <b style={{color: 'red'}}>{agingDays}</b> : agingDays;
			} else if(agingDays === 0) {
				return agingDays;
			} else if(agingDays < 0) {
				return '';
			}
		} else {
			const agingDays = moment(item.payment_date).diff(moment(item.due_date), 'days');
			if(agingDays > 0) {
				return style ? <b style={{color: 'red'}}>{agingDays}</b> : agingDays;
			} else {
				return '0';
			}
		}
	};

	
	// select style
	const selectStyle = useMemo(() => ({ container: provided => ({...provided, width: "150px", color: 'black'}) }), []);

	// material table
	const formatDate = dateString => moment(dateString).format('YYYY-MM-DD');
	const formatMonth = dateString => moment(dateString).format('YYYY-MM');
	const isOlderThan30Days = dateString => moment().diff(moment(dateString), 'days') > 30;
	const isGovDoc = rowData => rowData.contract_id === 'SCFITGY' && /-02$/g.test(rowData.proforma_inv_no);
	const isSCFIT = rowData => /SCFIT/gi.test(rowData.contract_id);
	const is01Doc = rowData => /-01$/g.test(rowData.proforma_inv_no);
	const originalData = (rowData, key) => paymentRecordData.find(item => item.id === rowData.id)[key];
	// material table columns
	const columns = [
		// not editable
		{ title: "Month", field: "dateData", render: item => formatMonth(item.dateData), editable: 'never' },
		{ title: "Entity", field: "entity_id", editable: 'never', render: item => formatOldEntityId(item.entity_id) },
		{ title: "Client", field: "company", editable: 'never' },
		{ title: "Site", field: "site_name", editable: 'never' },
		{ title: "SF site ID", field: "SF_project_id", editable: 'never' },
		{ title: "Proforma invoice no.", grouping: false, field: "proforma_inv_no", editable: 'never' },
		// only editable on gov records
		{ title: "Amount (VAT excluded)", grouping: false, field: "subtotal", render: item => formatNumber(item.subtotal, 0), editComponent: props => <input type="number" min="0" defaultValue={props.rowData.subtotal} disabled={!isGovDoc(props.rowData) || isOlderThan30Days(props.rowData.created_at)} onChange={e => props.onChange(e.target.value)} /> },
		{ title: "VAT", grouping: false, field: "vat_amount", render: item => formatNumber(item.vat_amount, 0),  editComponent: props => <input type="number" min="0" defaultValue={props.rowData.vat_amount} disabled={!isGovDoc(props.rowData) || isOlderThan30Days(props.rowData.created_at)} onChange={e => props.onChange(e.target.value)} /> },
		{ title: "Amount (VAT included)", grouping: false, field: "total", render: item => formatNumber(item.total, 0), editComponent: props => <input type="number" min="0" defaultValue={props.rowData.total} disabled={!isGovDoc(props.rowData) || isOlderThan30Days(props.rowData.created_at)} onChange={e => props.onChange(e.target.value)} /> },
		{ title: "Sharing Revenue Amount", grouping: false, field: "share_rev_amt", render: item => formatNumber(item.share_rev_amt, 0),  editComponent: props => <input type="number" defaultValue={props.rowData.share_rev_amt || null} disabled={!(isSCFIT(props.rowData) && is01Doc(props.rowData)) || (originalData(props.rowData, 'share_rev_amt') && isOlderThan30Days(props.rowData.created_at))} onChange={e => {console.log(props); props.onChange(e.target.value)}} /> },
		// editable regardless of contract
		{ title: "VAT invoice no.", grouping: false, field: "vat_inv_no", render: item => item.vat_inv_no || '-', type: 'text', editComponent: props => <input type="text" disabled={isOlderThan30Days(props.rowData.created_at)} defaultValue={props.rowData.vat_inv_no} onChange={e => props.onChange(e.target.value)} /> },
		{ title: "VAT invoice issue date", grouping: false, field: "vat_inv_date", render: item => !!item.vat_inv_date ? formatDate(item.vat_inv_date) : '-', editComponent: props => <input type="date" disabled={isOlderThan30Days(props.rowData.created_at)} defaultValue={moment(props.rowData.vat_inv_date).format('YYYY-MM-DD')} max={moment().format('YYYY-MM-DD')} onChange={e => props.onChange(e.target.value)} /> },
		{ title: "Payment term (days)", grouping: false, field: "credit_term", editComponent: props => <input type="number" step="1" min="0" disabled={isOlderThan30Days(props.rowData.created_at)} defaultValue={props.rowData.credit_term} onChange={e => props.onChange(e.target.value)} /> },
		{ title: "Due date", grouping: false, field: "due_date", render: item => formatDate(item.due_date), editable: 'never' },
		{ title: "Payment date", grouping: false, field: "payment_date", render: item => item.payment_date && formatDate(item.payment_date), editComponent: props => <input disabled={originalData(props.rowData, 'payment_date') && props.rowData.payment_updated_at && isOlderThan30Days(props.rowData.payment_updated_at)} type="date" defaultValue={moment(props.rowData.payment_date).format('YYYY-MM-DD')} min={moment(props.rowData.vat_inv_date).format('YYYY-MM-DD')} onChange={e => props.onChange(e.target.value)} /> },
		{ title: "AR aging (days)", grouping: false, field: "ar_aging", render: (item, conf) => getARAging(item, conf), editable: 'never' },
	];

	const csvFilename = useMemo(() => `ar-aging-report-${filter.agingDate.format('YYYY-MM-DD')}.csv`, [filter]);
	const csvData = useMemo(() => {
		if(isLoading || !paymentRecordData.length) { return null }
		const data = [];
		data.push([`GYSS AR AGING REPORT AS OF ${filter.agingDate.format('MMM D, YYYY')}`]);
		data.push([]);
		data.push(columns.map(column => column.title));
		data.push(...paymentRecordData.map(row => columns.map(col => {
			if(col.render) { return col.render(row, { style: false }) }
			return row[col.field];
		})));
		return data;
	}, [ paymentRecordData, isLoading, columns, filter.agingDate ]);

	const strToMoment = dateString => {
		if(!dateString) { return null }
		if(/^\d{4}-\d{2}-\d{2}$/gi.test(dateString)) { return moment(dateString, 'YYYY-MM-DD') }
		return moment(dateString);
	};

	const validateInput = async (newData, oldData) => {
		const new_vatInvNo = newData.vat_inv_no?.trim()
		const new_vatInvDate = newData.vat_inv_date;
		const new_subtotal = newData.subtotal;
		const new_credit_term = newData.credit_term;
		const new_vat_amount = newData.vat_amount;
		const new_total = newData.total;
		// const new_share_rev_amt = newData.share_rev_amt;
		if(!new_vatInvNo || !/^\d{1,8}$/gi.test(new_vatInvNo)) { throw new Error('Invalid VAT invoice number') }
		if(!new_vatInvDate) { throw new Error('Invalid VAT invoice issue date') }
		if(isNaN(parseFloat(new_subtotal)) || parseFloat(new_subtotal) < 0) { throw new Error('Subtotal can not be negative') }
		if(isNaN(parseFloat(new_credit_term)) || parseInt(new_credit_term) < 0) { throw new Error('Credit term must not be empty or negative') }
		if(isNaN(parseFloat(new_vat_amount)) || parseFloat(new_vat_amount) < 0) { throw new Error('VAT amount must not be empty or negative') }
		if(isNaN(parseFloat(new_total)) || parseFloat(new_total) < 0) { throw new Error('Total must not be empty or negative') }
		// if(isGovDoc(oldData) && isNaN(parseFloat(new_share_rev_amt))) { throw new Error('Sharing revenue amount must not be empty') }
		if((parseFloat(new_subtotal) + parseFloat(new_vat_amount)).toFixed(2) !== parseFloat(new_total).toFixed(2)) { throw new Error('Subtotal + VAT amount !== Total') }
	}

	const updatePaymentRecord = async (newData, oldData) => {
		const id = newData.id;
		const payment_date = strToMoment(newData.payment_date);
		const old_payment_date = strToMoment(oldData.payment_date);
		const subtotal = parseFloat(newData.subtotal);
		const vat_amount = parseFloat(newData.vat_amount);
		const total = parseFloat(newData.total);
		const share_rev_amt = !isNaN(parseFloat(newData.share_rev_amt)) ? parseFloat(newData.share_rev_amt) : null;
		const credit_term = parseInt(newData.credit_term);
		const vat_inv_no = newData.vat_inv_no.trim()
		const new_vatInvDate = strToMoment(newData.vat_inv_date);
		const vat_inv_date = new_vatInvDate && new_vatInvDate.format('YYYY-MM-DD');
		const vat_updated_by = user.email;

		const payload = {};
		payload.vat_inv = { vat_inv_no, vat_inv_date, vat_updated_by, subtotal, vat_amount, total, share_rev_amt, credit_term };

		if(payment_date?.unix() !== old_payment_date?.unix() || parseInt(credit_term) !== parseInt(oldData.credit_term)) {
			const payment_date_str = payment_date?.format('YYYY-MM-DD');
			const payment_updated_by = user.email;
			payload.payment = { payment_date: payment_date_str, credit_term, payment_updated_by };
		}

		if(payload.vat_inv || payload.payment) {
			await GYAxios.patch(`/payment_record_vn/${id}`, payload);
		}
	};

	const fetchData = useCallback(async () => {
		// filter validation before fetch
		if(!filter.monthStart && filter.monthEnd) { return }
		if(filter.monthStart && !filter.monthEnd) { return }
		if(filter.paymentStatus !== 'OVERDUED' && Object.keys(filter).filter(k => k !== 'agingDate').every(k => filter[k] === prevFilter?.current?.[k])) {
			return;
		}
		// really fetch data
		setIsLoading(true);
		try {
			const monthRange = (filter.monthStart && filter.monthEnd) ? `${filter.monthStart.format('YYYYMM')},${filter.monthEnd.format('YYYYMM')}` : null;
			const queryStringObj = { ...filter, agingDate: filter.agingDate.format('YYYY-MM-DD'), month: monthRange };
			const queryString = stringify(queryStringObj);
			const res = await GYAxios.get(`/payment_record_vn/?${queryString}`);
			setPaymentRecordData(res.data);
		} catch(err) {
			console.log(err);
			Noti.create({ type: 'e', msg: 'Error while fetching payment records' });
		}
		setIsLoading(false);
		prevFilter.current = filter;
	}, [ filter ]);

	const fetchClientSelect = async () => {
		try {
			const res = await GYAxios.get('/select/clients/VN');
			const options = [{ label: 'All Clients', value: '_ALL' }, ...res.data];
			setClientOptions(options);
		} catch(error) {
			console.log(error);
			Noti.create({ type: 'e', msg: 'Error while fetching client list' });
		}
	}

	const fetchSolarEntitySelect = async () => {
		try {
			const res = await GYAxios.get('/select/entities/VN');
			let options = [allEntityOpt, ...res.data];
			options = options.filter(oldEntityFilter);
			setSolarEntityOptions(options);
		} catch(err) {
			console.log(err);
			Noti.create({ type: 'e', msg: 'Error while fetching client list' });
		}
	}

	useEffect(() => {
		if(!firstFetch.current) { return }
		const timeout = setTimeout(() => {
			firstFetch.current = false;
			fetchData();
			fetchClientSelect();
			fetchSolarEntitySelect();
		}, 100);
		return () => clearTimeout(timeout);
	}, [ fetchData ]);

	useEffect(() => {
		fetchData();
	}, [ fetchData ]);

	return (
		<>
			<div className="perf-table panel panel-default">
				<div className="panel-heading ">
					<Row>
						<Col md="11">
							<h2 className="panel-title" id="date">
								Payment Record
							</h2>
							<br />
								<div className={style.toolbarContainer}>

								{/* filter */}
								<div className={style.filterContainer}>
									{/* month picker start */}
									<div>
										Filter by month<br/>
										<div className={style.datePicker}>
											<DatePicker
												maxDate={localTimeToPickerTime(filterMonthMax)}
												selected={filter.monthStart && localTimeToPickerTime(filter.monthStart.toDate())}
												dateFormat="yyyy-MM"
												showMonthYearPicker
												isClearable
												onChange={(date) => {
													const selectedDate = date && moment(pickerTimeTolocalTime(date));
													let monthEnd = filter.monthEnd;
													if((monthEnd && selectedDate && selectedDate.isAfter(monthEnd)) || !monthEnd) {
														monthEnd = selectedDate?.clone();
													}
													setFilter({ ...filter, monthStart: selectedDate, monthEnd });
												}}
												placeholderText="Month start"
											/>
										</div>
									</div>
									{/* month picker end */}
									<div>
										<br/>
										<div className={style.datePicker}>
											<DatePicker
												maxDate={localTimeToPickerTime(filterMonthMax)}
												selected={filter.monthEnd && localTimeToPickerTime(filter.monthEnd.toDate())}
												dateFormat="yyyy-MM"
												showMonthYearPicker
												isClearable
												onChange={(date) => {
													const selectedDate = date && moment(pickerTimeTolocalTime(date));
													let monthStart = filter.monthStart;
													if((monthStart && selectedDate && selectedDate.isBefore(monthStart)) || !monthStart) {
														monthStart = selectedDate?.clone();
													}
													setFilter({ ...filter, monthEnd: selectedDate, monthStart });
												}}
												placeholderText="Month end"
											/>
										</div>
									</div>

									{/* client select */}
									<div>
										Filter by client<br/>
										<ReactSelect options={clientOptions} onChange={option => setFilter({ ...filter, client: option.value })} value={clientOptions.find(option => option.value === filter.client)} styles={selectStyle} />
									</div>

									{/* solar entity select */}
									<div>
										Filter by solar entity<br/>
										<ReactSelect options={solarEntityOptions} onChange={option => setFilter({ ...filter, solarEntity: option.value })} value={solarEntityOptions.find(option => option.value === filter.solarEntity)} styles={selectStyle} />
									</div>

									{/* payment staus */}
									<div>
										Filter by payment status<br/>
										<ReactSelect options={paymentStatusOptions} onChange={option => setFilter({ ...filter, paymentStatus: option.value })} value={paymentStatusOptions.find(option => option.value === filter.paymentStatus)} styles={selectStyle} />
									</div>
								</div>

								{/* Aging date select */}
								<div>
									Date for Aging Day<br/>
									<div className={style.datePicker}>
										<DatePicker
											selected={localTimeToPickerTime(filter.agingDate.toDate())}
											onChange={(date) => {
												const agingDate = moment(pickerTimeTolocalTime(date));
												setFilter({
													...filter,
													agingDate
												});
											}}
											dateFormat="yyyy-MM-dd"
										/>
									</div>
								</div>
						
								{/* excel download button */}
								<div>
									{csvData && <CSVLink data={csvData} filename={csvFilename}><button className="btn btn-primary btn" style={{ marginTop: '1.6em', color: 'white' }}>
										<span class="glyphicon glyphicon-download-alt" style={{marginRight: '0.5em'}}></span>
										<span class={style.downloadText}>Download</span>
									</button></CSVLink>}
								</div>

							</div>
								
						</Col>
						<Col md="1" style={{ textAlign: "right" }}>
							<img
								src={`${directory}/images/reload.png`}
								onClick={() => fetchData()}
								style={{
									height: "35px",
									cursor: "pointer",
								}}
								alt="logo"
							/>
						</Col>
					</Row>
				</div>
				<div className="panel-body">
					<MaterialTable
						columns={columns}
						data={paymentRecordData}
						isLoading={isLoading}
						options={{
							showTitle: false,
							grouping: true,
							pageSize: 10,
							pageSizeOptions: [10, 20, 40, 60],
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
							cellStyle: {
								textAlign: "center",
							},
							actionsColumnIndex: -1,
						}}
						editable={{
							isEditable: rowData => true,
							onRowUpdate: async (newData, oldData) => {
								try {
									await validateInput(newData, oldData);
								} catch(err) {
									Noti.create({ type: 'e', msg: err.message });
									throw err;
								}
								await updatePaymentRecord(newData, oldData);
								await fetchData();
							}
						}}
					/>
				</div>
			</div>
			<Noti />
		</>
	);
};

export default PaymentRecordVN;
